import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Grid,
  CircularProgress,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import SecurityIcon from '@mui/icons-material/Security';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../context/AuthContext';
import { 
  getMetodosPago, 
  createMetodoPago, 
  updateMetodoPago, 
  deleteMetodoPago,
  setMetodoPagoPrincipal,
  MetodoPago,
  getMetodoPagoLabel
} from '../../services/metodosPago';

// ========== TIPOS DE MARCAS DE TARJETA ==========
type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | 'unionpay' | 'maestro' | 'unknown';

interface CardBrandInfo {
  name: string;
  color: string;
  bgColor: string;
  icon: string;
  cvvLength: number;
  maxLength: number;
}

// ========== INFORMACIÓN DE MARCAS DE TARJETA ==========
const CARD_BRANDS: Record<CardBrand, CardBrandInfo> = {
  visa: {
    name: 'Visa',
    color: '#1A1F71',
    bgColor: '#1A1F71',
    icon: '💳',
    cvvLength: 3,
    maxLength: 16
  },
  mastercard: {
    name: 'Mastercard',
    color: '#EB001B',
    bgColor: 'linear-gradient(135deg, #EB001B 0%, #F79E1B 100%)',
    icon: '💳',
    cvvLength: 3,
    maxLength: 16
  },
  amex: {
    name: 'American Express',
    color: '#006FCF',
    bgColor: '#006FCF',
    icon: '💳',
    cvvLength: 4,
    maxLength: 15
  },
  discover: {
    name: 'Discover',
    color: '#FF6000',
    bgColor: '#FF6000',
    icon: '💳',
    cvvLength: 3,
    maxLength: 16
  },
  diners: {
    name: 'Diners Club',
    color: '#0079BE',
    bgColor: '#0079BE',
    icon: '💳',
    cvvLength: 3,
    maxLength: 14
  },
  jcb: {
    name: 'JCB',
    color: '#0E4C96',
    bgColor: 'linear-gradient(135deg, #0E4C96 0%, #BC9B1A 100%)',
    icon: '💳',
    cvvLength: 3,
    maxLength: 16
  },
  unionpay: {
    name: 'UnionPay',
    color: '#E21836',
    bgColor: 'linear-gradient(135deg, #E21836 0%, #002B5C 100%)',
    icon: '💳',
    cvvLength: 3,
    maxLength: 19
  },
  maestro: {
    name: 'Maestro',
    color: '#0066CC',
    bgColor: 'linear-gradient(135deg, #0066CC 0%, #CC0000 100%)',
    icon: '💳',
    cvvLength: 3,
    maxLength: 19
  },
  unknown: {
    name: 'Tarjeta',
    color: '#666666',
    bgColor: '#666666',
    icon: '💳',
    cvvLength: 3,
    maxLength: 19
  }
};

// ========== FUNCIÓN PARA DETECTAR MARCA DE TARJETA ==========
const detectCardBrand = (cardNumber: string): CardBrand => {
  // Limpiar el número de tarjeta
  const cleanNumber = cardNumber.replace(/[\s-]/g, '');
  
  if (!cleanNumber || cleanNumber.length < 1) return 'unknown';
  
  // Visa: Comienza con 4
  if (/^4/.test(cleanNumber)) return 'visa';
  
  // Mastercard: Comienza con 51-55 o 2221-2720
  if (/^5[1-5]/.test(cleanNumber) || /^2(22[1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720)/.test(cleanNumber)) return 'mastercard';
  
  // American Express: Comienza con 34 o 37
  if (/^3[47]/.test(cleanNumber)) return 'amex';
  
  // Discover: Comienza con 6011, 622126-622925, 644-649, 65
  if (/^(6011|65|64[4-9]|622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9[01]\d|92[0-5]))/.test(cleanNumber)) return 'discover';
  
  // Diners Club: Comienza con 300-305, 309, 36, 38, 39
  if (/^(30[0-5]|309|36|3[89])/.test(cleanNumber)) return 'diners';
  
  // JCB: Comienza con 3528-3589
  if (/^35(2[89]|[3-8]\d)/.test(cleanNumber)) return 'jcb';
  
  // UnionPay: Comienza con 62 (excepto los rangos de Discover)
  if (/^62(?!(2(12[6-9]|1[3-9]\d|[2-8]\d{2}|9[01]\d|92[0-5])))/.test(cleanNumber)) return 'unionpay';
  
  // Maestro: Comienza con 50, 56-69
  if (/^(50|5[6-9]|6[0-9])/.test(cleanNumber) && !/^(6011|65|64[4-9])/.test(cleanNumber)) return 'maestro';
  
  return 'unknown';
};

// ========== COMPONENTE VISUAL DE MARCA DE TARJETA ==========
const CardBrandBadge: React.FC<{ brand: CardBrand; size?: 'small' | 'medium' | 'large' }> = ({ brand, size = 'medium' }) => {
  const brandInfo = CARD_BRANDS[brand];
  const sizes = {
    small: { width: 40, height: 26, fontSize: '0.6rem' },
    medium: { width: 56, height: 35, fontSize: '0.7rem' },
    large: { width: 70, height: 44, fontSize: '0.8rem' }
  };
  const sizeConfig = sizes[size];
  
  if (brand === 'unknown') {
    return (
      <Box sx={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        borderRadius: '4px',
        bgcolor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #ddd'
      }}>
        <CreditCardIcon sx={{ fontSize: sizeConfig.fontSize === '0.6rem' ? 16 : 20, color: '#999' }} />
      </Box>
    );
  }
  
  return (
    <Box sx={{
      width: sizeConfig.width,
      height: sizeConfig.height,
      borderRadius: '6px',
      background: brandInfo.bgColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: sizeConfig.fontSize,
      letterSpacing: '0.5px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      textTransform: 'uppercase'
    }}>
      {brand === 'visa' && 'VISA'}
      {brand === 'mastercard' && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#EB001B', mr: '-4px' }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#F79E1B' }} />
        </Box>
      )}
      {brand === 'amex' && 'AMEX'}
      {brand === 'discover' && 'DISC'}
      {brand === 'diners' && 'DC'}
      {brand === 'jcb' && 'JCB'}
      {brand === 'unionpay' && 'UP'}
      {brand === 'maestro' && 'Maestro'}
    </Box>
  );
};

const PaymentCard = styled(Card)({
  borderRadius: '15px',
  boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
  },
});

const AddPaymentCard = styled(Card)({
  borderRadius: '15px',
  border: '2px dashed #2e5a33',
  backgroundColor: 'rgba(46, 90, 51, 0.02)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(46, 90, 51, 0.05)',
    borderColor: '#1a3d1e',
  },
});

const PaymentOptionButton = styled(Box)<{ bgcolor: string }>(({ bgcolor }) => ({
  padding: '16px 24px',
  borderRadius: '8px',
  backgroundColor: bgcolor,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  },
}));

// Funciones auxiliares (movidas fuera del componente)
const getPaymentIcon = (tipo: MetodoPago['tipo']) => {
  switch (tipo) {
    case 'google_pay':
      return <PaymentIcon sx={{ fontSize: 40 }} />;
    case 'mercado_pago':
      return <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />;
    default:
      return <CreditCardIcon sx={{ fontSize: 40 }} />;
  }
};

const getPaymentColor = (tipo: MetodoPago['tipo']) => {
  switch (tipo) {
    case 'google_pay':
      return '#4285f4';
    case 'mercado_pago':
      return '#00a8ff';
    default:
      return '#2e5a33';
  }
};

const getPaymentLabel = (tipo: MetodoPago['tipo']) => {
  switch (tipo) {
    case 'google_pay':
      return 'Google Pay';
    case 'mercado_pago':
      return 'Mercado Pago';
    case 'tarjeta_credito':
      return 'Tarjeta de Crédito';
    case 'tarjeta_debito':
      return 'Tarjeta de Débito';
    default:
      return tipo;
  }
};

const CustomerPayments: React.FC = () => {
  const { user } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState<MetodoPago[]>([]);
  const [bancos, setBancos] = useState<string[]>([
    'Banco de Chile',
    'Banco Estado',
    'Banco Santander',
    'Banco BCI',
    'Banco Scotiabank',
    'Banco Itaú',
    'Banco Security',
    'Banco Falabella',
    'Banco Ripley',
    'Coopeuch',
    'Otro'
  ]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [cardFormOpen, setCardFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingMethod, setEditingMethod] = useState<MetodoPago | null>(null);
  const [formData, setFormData] = useState({
    tipo: 'tarjeta_credito' as MetodoPago['tipo'],
    banco: '',
    ultimos_digitos: '',
    titular: '',
    mes_expiracion: '',
    anio_expiracion: '',
    es_principal: false,
    apodo: '',
    email_wallet: '',
    verificado: false,
  });
  const [cardNumber, setCardNumber] = useState('');
  const [cvv, setCvv] = useState('');
  const [showMPHelp, setShowMPHelp] = useState(false);
  const [googlePayLoading, setGooglePayLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Detectar marca de tarjeta automáticamente
  const detectedCardBrand = useMemo(() => detectCardBrand(cardNumber), [cardNumber]);
  const cardBrandInfo = CARD_BRANDS[detectedCardBrand];

  // Cargar métodos de pago desde Django
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log('📥 [MÉTODOS PAGO] Cargando desde Django...');
        const methods = await getMetodosPago();
        setPaymentMethods(methods);
        console.log(`✅ [MÉTODOS PAGO] ${methods.length} métodos cargados`);
        
        // Verificar si debe mostrar mensaje de ayuda de MercadoPago
        const mpHelpStr = localStorage.getItem('mp_help_message');
        if (mpHelpStr) {
          const mpHelp = JSON.parse(mpHelpStr);
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
          if (mpHelp.timestamp > fiveMinutesAgo) {
            setShowMPHelp(true);
            localStorage.removeItem('mp_help_message');
          }
        }
      } catch (error) {
        console.error('❌ [MÉTODOS PAGO] Error al cargar:', error);
        setSnackbar({
          open: true,
          message: 'Error al cargar métodos de pago',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, [user]);

  // Detectar retorno de Mercado Pago después de vinculación
  useEffect(() => {
    const checkMercadoPagoReturn = async () => {
      if (!user) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const mpStatus = urlParams.get('mp_status');
      const paymentId = urlParams.get('payment_id');
      const preferenceId = urlParams.get('preference_id') || urlParams.get('preference-id'); // Mercado Pago usa 'preference-id' con guión
      const externalReference = urlParams.get('external_reference');
      const collectionId = urlParams.get('collection_id');
      const collectionStatus = urlParams.get('collection_status');
      const status = urlParams.get('status');
      
      console.log('🔍 [MERCADO PAGO] Verificando retorno...', { 
        mpStatus, 
        paymentId, 
        preferenceId, 
        externalReference,
        collectionId,
        collectionStatus,
        status,
        fullUrl: window.location.href 
      });
      
      // Verificar si hay datos de verificación pendientes
      const mpDataStr = localStorage.getItem('mp_verification_pending');
      
      if (!mpDataStr) {
        console.log('ℹ️ [MERCADO PAGO] No hay verificación pendiente');
        return;
      }
      
      console.log('📦 [MERCADO PAGO] Datos en localStorage:', mpDataStr);
      const mpData = JSON.parse(mpDataStr);
      
      // Verificar que coincida con el usuario actual
      if (mpData.userId !== user.id) {
        console.log('⚠️ [MERCADO PAGO] Usuario no coincide');
        return;
      }
      
      console.log('✅ [MERCADO PAGO] Usuario verificado');
      
      // Detectar retorno desde la URL de MercadoPago (pueden venir diferentes parámetros)
      const hasReturnParams = mpStatus || collectionStatus || preferenceId || status || paymentId;
      
      if (hasReturnParams) {
        console.log('✅ [MERCADO PAGO] Parámetros de retorno detectados, procesando...');
        
        // Determinar el estado del pago
        const paymentStatus = mpStatus || collectionStatus || status;
        const isSuccess = paymentStatus === 'approved' || paymentStatus === 'pending' || collectionStatus === 'approved';
        
        console.log('📊 [MERCADO PAGO] Estado del pago:', { paymentStatus, isSuccess });
        
        if (isSuccess) {
            // Crear el método de pago automáticamente
            try {
              console.log('💾 [MERCADO PAGO] Guardando método de pago...');
              
              // Verificar si ya existe un método de Mercado Pago
              const existingMethods = paymentMethods.filter(m => m.tipo === 'mercado_pago');
              if (existingMethods.length > 0) {
                console.log('⚠️ [MERCADO PAGO] Ya existe un método de Mercado Pago, omitiendo creación');
                setSnackbar({
                  open: true,
                  message: 'ℹ️ Ya tienes una cuenta de Mercado Pago vinculada',
                  severity: 'info'
                });
                localStorage.removeItem('mp_verification_pending');
                const cleanUrl = `${window.location.origin}${window.location.pathname}?tab=payments`;
                window.history.replaceState({}, document.title, cleanUrl);
                return;
              }
              
              // Crear método de pago usando el servicio de Django
              const newMethod = await createMetodoPago({
                tipo: 'mercado_pago',
                apodo: 'Mercado Pago',
                email_wallet: mpData.email || user.email || '',
                verificado: true,
                es_principal: paymentMethods.length === 0
              });
              
              console.log('✅ [MERCADO PAGO] Método guardado exitosamente:', newMethod);
              
              // Agregar a la lista local
              setPaymentMethods(prev => [...prev, newMethod]);
              
              setSnackbar({
                open: true,
                message: '✅ Cuenta de Mercado Pago vinculada exitosamente',
                severity: 'success'
              });
              
              // Limpiar localStorage
              localStorage.removeItem('mp_verification_pending');
              console.log('🗑️ [MERCADO PAGO] localStorage limpiado');
              
              // Limpiar URL (mantener solo tab=payments)
              const cleanUrl = `${window.location.origin}${window.location.pathname}?tab=payments`;
              window.history.replaceState({}, document.title, cleanUrl);
              console.log('🧹 [MERCADO PAGO] URL limpiada');
              
            } catch (error) {
              console.error('❌ [MERCADO PAGO] Error al guardar método:', error);
              setSnackbar({
                open: true,
                message: '❌ Error al guardar el método de pago',
                severity: 'error'
              });
            }
          } else {
            console.log('⚠️ [MERCADO PAGO] Verificación rechazada o cancelada');
            
            // Determinar mensaje específico según el estado
            let errorMessage = '❌ Verificación de Mercado Pago ';
            if (paymentStatus === 'rejected') {
              errorMessage += 'rechazada. Intenta con otra forma de pago o verifica tu cuenta de Mercado Pago.';
            } else if (paymentStatus === 'cancelled') {
              errorMessage += 'cancelada. Puedes intentar nuevamente cuando quieras.';
            } else {
              errorMessage += 'no completada. Puedes intentar nuevamente.';
            }
            
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: 'warning'
            });
            localStorage.removeItem('mp_verification_pending');
            const cleanUrl = `${window.location.origin}${window.location.pathname}?tab=payments`;
            window.history.replaceState({}, document.title, cleanUrl);
          }
      } else {
        console.log('ℹ️ [MERCADO PAGO] No hay parámetros de retorno en la URL');
      }
    };
    
    checkMercadoPagoReturn();
  }, [user, paymentMethods.length]);

  const handleAddNew = () => {
    setOptionsDialogOpen(true);
  };

  const handleSelectPaymentType = (tipo: MetodoPago['tipo']) => {
    setEditingMethod(null);
    setFormData({
      tipo,
      banco: '',
      ultimos_digitos: '',
      titular: '',
      mes_expiracion: '',
      anio_expiracion: '',
      es_principal: false,
      apodo: '',
      email_wallet: '',
      verificado: false,
    });
    setCardNumber('');
    setCvv('');
    setOptionsDialogOpen(false);
    
    // Si es Google Pay, verificar disponibilidad y vincular
    if (tipo === 'google_pay') {
      handleGooglePaySetup();
    } 
    // Si es Mercado Pago, configurar y abrir formulario
    else if (tipo === 'mercado_pago') {
      handleMercadoPagoSetup();
    }
    // Para tarjetas de crédito/débito, usar el nuevo formulario
    else if (tipo === 'tarjeta_credito' || tipo === 'tarjeta_debito') {
      setCardFormOpen(true);
    }
    // Para otros métodos, usar el formulario tradicional
    else {
      setDialogOpen(true);
    }
  };

  const handleGooglePaySetup = async () => {
    if (!user) {
      setSnackbar({
        open: true,
        message: '❌ Debes iniciar sesión para vincular Google Pay',
        severity: 'error'
      });
      return;
    }

    setGooglePayLoading(true);
    setOptionsDialogOpen(false);

    try {
      console.log('🔍 [GOOGLE PAY] Verificando disponibilidad...');
      
      // Verificar si Google Pay está disponible en el navegador
      if (typeof window === 'undefined' || !window.google?.payments?.api) {
        // Cargar el script de Google Pay si no está cargado
        console.log('📦 [GOOGLE PAY] Cargando API de Google Pay...');
        
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://pay.google.com/gp/p/js/pay.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('No se pudo cargar Google Pay'));
          document.body.appendChild(script);
        });
        
        // Esperar a que la API esté lista
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Crear cliente de pagos de Google
      const paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: 'TEST' // Cambiar a 'PRODUCTION' en producción
      });

      // Verificar si Google Pay está disponible
      const isReadyToPayRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
          type: 'CARD' as const,
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'] as ('PAN_ONLY' | 'CRYPTOGRAM_3DS')[],
            allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX'] as ('VISA' | 'MASTERCARD' | 'AMEX')[]
          }
        }]
      };

      const response = await paymentsClient.isReadyToPay(isReadyToPayRequest);
      
      if (!response.result) {
        setSnackbar({
          open: true,
          message: '❌ Google Pay no está disponible en este dispositivo o navegador',
          severity: 'error'
        });
        setGooglePayLoading(false);
        return;
      }

      console.log('✅ [GOOGLE PAY] Disponible, iniciando vinculación...');

      // Configurar la solicitud de pago
      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
          type: 'CARD' as const,
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'] as ('PAN_ONLY' | 'CRYPTOGRAM_3DS')[],
            allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX'] as ('VISA' | 'MASTERCARD' | 'AMEX')[]
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY' as const,
            parameters: {
              gateway: 'example', // En producción: tu gateway (mercadopago, stripe, etc.)
              gatewayMerchantId: 'exampleMerchantId'
            }
          }
        }],
        merchantInfo: {
          merchantId: 'BCR2DN4T5U7PBLZF', // ID de comerciante de Google (obtener de Google Pay Console)
          merchantName: 'El Piñón - Abarrotes'
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL' as const,
          totalPriceLabel: 'Verificación',
          totalPrice: '1.00',
          currencyCode: 'CLP',
          countryCode: 'CL'
        },
        emailRequired: true
      };

      // Solicitar datos de pago
      const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
      
      console.log('✅ [GOOGLE PAY] Datos obtenidos:', paymentData);
      
      // Extraer información
      const paymentMethodData = paymentData.paymentMethodData;
      const cardInfo = paymentMethodData.info;
      const email = paymentData.email || user.email;

      // Configurar el formulario con los datos de Google Pay
      setFormData({
        tipo: 'google_pay',
        banco: cardInfo.cardNetwork || 'Google Pay',
        ultimos_digitos: cardInfo.cardDetails || '****',
        titular: user.email || '',
        mes_expiracion: '',
        anio_expiracion: '',
        es_principal: paymentMethods.length === 0,
        apodo: `Google Pay (${cardInfo.cardNetwork || 'Tarjeta'} ****${cardInfo.cardDetails || ''})`,
        email_wallet: email,
        verificado: true,
      });

      setDialogOpen(true);
      
      setSnackbar({
        open: true,
        message: '✅ Cuenta de Google Pay vinculada. Completa los datos para guardar.',
        severity: 'success'
      });
      
    } catch (error: any) {
      console.error('❌ [GOOGLE PAY] Error:', error);
      
      // Si el usuario cancela, no mostrar error agresivo
      if (error.statusCode === 'CANCELED' || error.message?.includes('cancel')) {
        setSnackbar({
          open: true,
          message: '⚠️ Vinculación cancelada',
          severity: 'warning'
        });
      } else {
        // Para otros errores, ofrecer alternativa manual
        setFormData(prev => ({
          ...prev,
          tipo: 'google_pay',
          email_wallet: user?.email || '',
          verificado: false,
          apodo: 'Google Pay'
        }));
        setDialogOpen(true);
        
        setSnackbar({
          open: true,
          message: '⚠️ No se pudo conectar con Google Pay. Puedes vincular manualmente.',
          severity: 'warning'
        });
      }
    } finally {
      setGooglePayLoading(false);
    }
  };

  const handleMercadoPagoSetup = async () => {
    if (!user) {
      setSnackbar({
        open: true,
        message: '❌ Debes iniciar sesión para vincular Mercado Pago',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('🔍 [MERCADO PAGO] Iniciando vinculación...');
      console.log('👤 [MERCADO PAGO] Usuario:', user.id, user.email);
      
      setLoading(true);
      
      // Guardar datos para verificación después del retorno
      const verificationData = {
        userId: user.id,
        email: user.email,
        timestamp: Date.now()
      };
      localStorage.setItem('mp_verification_pending', JSON.stringify(verificationData));
      
      // Importar api dinámicamente
      const api = (await import('../../services/api')).default;
      
      // Crear preferencia de pago para verificación (monto mínimo que funcione)
      const response = await api.post('/pagos/mercadopago/create_preference/', {
        items: [{
          title: 'Verificación de cuenta Mercado Pago',
          quantity: 1,
          unit_price: 1, 
        }],
        order_id: `verificacion_mp_${Date.now()}`,
        payer: {
          email: user.email,
          name: user.first_name || user.username,
        }
      });
      
      console.log('✅ [MERCADO PAGO] Preferencia creada:', response.data);
      
      if (response.data.init_point) {
        // Redirigir al checkout de MercadoPago
        window.location.href = response.data.init_point;
      } else {
        throw new Error('No se recibió URL de pago');
      }
      
    } catch (error: any) {
      console.error('❌ [MERCADO PAGO] Error al vincular:', error);
      setLoading(false);
      localStorage.removeItem('mp_verification_pending');
      setSnackbar({
        open: true,
        message: `❌ Error al vincular Mercado Pago: ${error.response?.data?.error || error.message || 'Error desconocido'}`,
        severity: 'error'
      });
    }
  };

  const handleEdit = (method: MetodoPago) => {
    setEditingMethod(method);
    setFormData({
      tipo: method.tipo,
      banco: method.banco || '',
      ultimos_digitos: method.ultimos_digitos || '',
      titular: method.titular || '',
      mes_expiracion: method.mes_expiracion || '',
      anio_expiracion: method.anio_expiracion || '',
      es_principal: method.es_principal || false,
      apodo: method.apodo || '',
      email_wallet: method.email_wallet || '',
      verificado: method.verificado || false,
    });
    setCardNumber('**** **** **** ' + (method.ultimos_digitos || ''));
    setCvv('');
    setDialogOpen(true);
  };

  const handleDelete = async (methodId: number) => {
    if (!user) return;
    
    if (window.confirm('¿Estás seguro de que quieres eliminar este método de pago?')) {
      try {
        console.log('🗑️ [MÉTODOS PAGO] Eliminando:', methodId);
        await deleteMetodoPago(methodId);
        setPaymentMethods(prev => prev.filter(method => method.id !== methodId));
        console.log('✅ [MÉTODOS PAGO] Eliminado exitosamente');
        setSnackbar({
          open: true,
          message: '✅ Método de pago eliminado exitosamente',
          severity: 'success'
        });
      } catch (error) {
        console.error('❌ [MÉTODOS PAGO] Error al eliminar:', error);
        setSnackbar({
          open: true,
          message: '❌ Error al eliminar el método de pago',
          severity: 'error'
        });
      }
    }
  };

  const handleSetDefault = async (methodId: number) => {
    if (!user) return;

    try {
      console.log('⭐ [MÉTODOS PAGO] Estableciendo predeterminado:', methodId);
      
      await setMetodoPagoPrincipal(methodId);

      setPaymentMethods(prev => prev.map(method => ({
        ...method,
        es_principal: method.id === methodId
      })));
      
      console.log('✅ [MÉTODOS PAGO] Predeterminado actualizado');
      setSnackbar({
        open: true,
        message: '✅ Método de pago predeterminado actualizado',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ [MÉTODOS PAGO] Error al actualizar predeterminado:', error);
      setSnackbar({
        open: true,
        message: '❌ Error al actualizar el método predeterminado',
        severity: 'error'
      });
    }
  };

  const validateCardNumber = (number: string): boolean => {
    // Eliminar espacios y guiones
    const cleanNumber = number.replace(/[\s-]/g, '');
    
    // Verificar que solo contenga números
    if (!/^\d+$/.test(cleanNumber)) return false;
    
    // Verificar longitud (13-19 dígitos para tarjetas válidas)
    if (cleanNumber.length < 13 || cleanNumber.length > 19) return false;
    
    // Algoritmo de Luhn
    let sum = 0;
    let isEven = false;
    
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber[i]);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    return sum % 10 === 0;
  };

  // TODO: Implementar PaymentMethodForm cuando esté disponible
  /*
  // Callback cuando se agrega una tarjeta exitosamente desde PaymentMethodForm
  const handleCardAdded = async (newCard: PaymentMethodType) => {
    if (!user) return;
    
    try {
      const newMethod = await createMetodoPago({
        tipo: newCard.tipo === 'tarjeta_credito' ? 'tarjeta_credito' : 'tarjeta_debito',
        apodo: newCard.apodo,
        banco: newCard.banco,
        ultimos_digitos: newCard.ultimos_digitos,
        titular: newCard.titular,
        mes_expiracion: newCard.mes_expiracion,
        anio_expiracion: newCard.anio_expiracion,
        es_principal: paymentMethods.length === 0
      });

      setPaymentMethods(prev => [...prev, newMethod]);
      setCardFormOpen(false);
      
      setSnackbar({
        open: true,
        message: '✅ Tarjeta agregada exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al agregar tarjeta:', error);
      setSnackbar({
        open: true,
        message: '❌ Error al agregar tarjeta',
        severity: 'error'
      });
    }
  };
  */

  // Eliminar una tarjeta
  const handleDeleteCard = async (cardId: number) => {
    if (!user) return;
    
    if (!window.confirm('¿Estás seguro de eliminar esta tarjeta?')) {
      return;
    }

    try {
      await deleteMetodoPago(cardId);
      setPaymentMethods(prev => prev.filter(m => m.id !== cardId));
      
      setSnackbar({
        open: true,
        message: '✅ Tarjeta eliminada',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al eliminar tarjeta:', error);
      setSnackbar({
        open: true,
        message: '❌ Error al eliminar tarjeta',
        severity: 'error'
      });
    }
  };

  // Establecer tarjeta como predeterminada
  const handleSetDefaultCard = async (cardId: number) => {
    if (!user) return;

    try {
      await setMetodoPagoPrincipal(cardId);

      // Actualizar el estado local
      setPaymentMethods(prev => prev.map(m => ({
        ...m,
        es_principal: m.id === cardId
      })));

      setSnackbar({
        open: true,
        message: '✅ Tarjeta predeterminada actualizada',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al establecer tarjeta predeterminada:', error);
      setSnackbar({
        open: true,
        message: '❌ Error al actualizar tarjeta',
        severity: 'error'
      });
    }
  };

  const handleSave = async () => {
    if (!user) return;

    // Validaciones según tipo
    const isWallet = formData.tipo === 'google_pay' || formData.tipo === 'mercado_pago';
    
    if (isWallet) {
      // Validación para wallets digitales
      if (!formData.email_wallet) {
        setSnackbar({
          open: true,
          message: '⚠️ Por favor ingresa el email de tu cuenta',
          severity: 'error'
        });
        return;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_wallet)) {
        setSnackbar({
          open: true,
          message: '⚠️ Email inválido',
          severity: 'error'
        });
        return;
      }
    } else {
      // Validación para tarjetas
      if (!formData.titular || !formData.banco || !formData.mes_expiracion || !formData.anio_expiracion) {
        setSnackbar({
          open: true,
          message: '⚠️ Por favor completa todos los campos obligatorios',
          severity: 'error'
        });
        return;
      }

      // Validar número de tarjeta solo para nuevas tarjetas
      if (!editingMethod) {
        const cleanCardNumber = cardNumber.replace(/[\s-]/g, '');
        
        if (!validateCardNumber(cleanCardNumber)) {
          setSnackbar({
            open: true,
            message: '⚠️ Número de tarjeta inválido',
            severity: 'error'
          });
          return;
        }

        // Validar CVV según marca de tarjeta
        const expectedCvvLength = cardBrandInfo.cvvLength;
        if (!cvv || cvv.length !== expectedCvvLength) {
          setSnackbar({
            open: true,
            message: `⚠️ CVV inválido (${expectedCvvLength} dígitos para ${cardBrandInfo.name})`,
            severity: 'error'
          });
          return;
        }

        // Guardar últimos 4 dígitos y marca detectada
        formData.ultimos_digitos = cleanCardNumber.slice(-4);
        
        // Guardar la marca detectada en el campo banco si no hay banco seleccionado
        if (!formData.banco && detectedCardBrand !== 'unknown') {
          formData.banco = cardBrandInfo.name;
        }
      }

      // Validar fecha de expiración
      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;
      const expYear = parseInt(formData.anio_expiracion!);
      const expMonth = parseInt(formData.mes_expiracion!);

      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        setSnackbar({
          open: true,
          message: '⚠️ La tarjeta está vencida',
          severity: 'error'
        });
        return;
      }
    }

    try {
      console.log('💾 [MÉTODOS PAGO] Guardando...', formData);

      if (editingMethod) {
        // Editar método existente
        console.log('📝 [MÉTODOS PAGO] Actualizando:', editingMethod.id);
        const updatedMethod = await updateMetodoPago(editingMethod.id, formData);

        setPaymentMethods(prev => prev.map(method => 
          method.id === editingMethod.id ? updatedMethod : method
        ));
        
        console.log('✅ [MÉTODOS PAGO] Actualizado exitosamente');
        setSnackbar({
          open: true,
          message: '✅ Método de pago actualizado exitosamente',
          severity: 'success'
        });
      } else {
        // Agregar nuevo método
        console.log('➕ [MÉTODOS PAGO] Agregando nuevo método');
        const newMethodData = {
          ...formData,
          es_principal: paymentMethods.length === 0,
          verificado: isWallet ? true : false, // Wallets se marcan como verificados automáticamente
        };

        const newMethod = await createMetodoPago(newMethodData);
        
        setPaymentMethods(prev => [...prev, newMethod]);
        
        console.log('✅ [MÉTODOS PAGO] Agregado exitosamente, ID:', newMethod.id);
        
        const mensajeExito = isWallet 
          ? `✅ ${getPaymentLabel(formData.tipo)} vinculado exitosamente`
          : '✅ Nueva tarjeta agregada exitosamente';
          
        setSnackbar({
          open: true,
          message: mensajeExito,
          severity: 'success'
        });
      }

      setDialogOpen(false);
      setCardNumber('');
      setCvv('');
    } catch (error) {
      console.error('❌ [MÉTODOS PAGO] Error al guardar:', error);
      setSnackbar({
        open: true,
        message: `❌ Error: ${error instanceof Error ? error.message : 'Error al guardar'}`,
        severity: 'error'
      });
    }
  };

  const handleChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCardNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/\s/g, '');
    
    // Solo permitir números
    if (!/^\d*$/.test(value)) return;
    
    // Limitar a 16 dígitos
    if (value.length > 16) return;
    
    // Formatear con espacios cada 4 dígitos
    const formatted = value.replace(/(\d{4})/g, '$1 ').trim();
    setCardNumber(formatted);
  };

  const handleCvvChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // Solo permitir números
    if (!/^\d*$/.test(value)) return;
    
    // Limitar a 4 dígitos
    if (value.length > 4) return;
    
    setCvv(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#2e5a33' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alerta si hay pago pendiente de MercadoPago */}
      {localStorage.getItem('mp_verification_pending') && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: '15px' }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => {
                const mpData = JSON.parse(localStorage.getItem('mp_verification_pending') || '{}');
                if (mpData.email) {
                  // Crear método manualmente
                  handleSelectPaymentType('mercado_pago');
                  localStorage.removeItem('mp_verification_pending');
                }
              }}
            >
              Completar Vinculación
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            ¡Tu pago de MercadoPago fue exitoso!
          </Typography>
          <Typography variant="body2">
            Haz clic en "Completar Vinculación" para terminar de agregar tu cuenta de MercadoPago.
          </Typography>
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
          Métodos de Pago
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{
            background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
            },
          }}
        >
          Agregar Método de Pago
        </Button>
      </Box>

      <Grid container spacing={3}>
        {paymentMethods.map((method) => (
          <Grid size={{ xs: 12, md: 6 }} key={method.id}>
            <PaymentCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: getPaymentColor(method.tipo) }}>
                      {getPaymentIcon(method.tipo)}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {method.apodo || getPaymentLabel(method.tipo)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {method.tipo === 'google_pay' || method.tipo === 'mercado_pago' 
                          ? method.email_wallet 
                          : method.banco}
                      </Typography>
                    </Box>
                    {method.es_principal && (
                      <Chip
                        icon={<StarIcon />}
                        label="Predeterminada"
                        size="small"
                        sx={{ bgcolor: '#2e5a33', color: 'white', ml: 1 }}
                      />
                    )}
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(method)}
                      sx={{ color: '#2e5a33' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(method.id)}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                {(method.tipo === 'tarjeta_credito' || method.tipo === 'tarjeta_debito') && (
                  <>
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      background: method.banco?.toLowerCase().includes('visa') 
                        ? 'linear-gradient(135deg, #1A1F71 0%, #3B5998 100%)'
                        : method.banco?.toLowerCase().includes('mastercard')
                          ? 'linear-gradient(135deg, #EB001B 0%, #F79E1B 100%)'
                          : method.banco?.toLowerCase().includes('amex') || method.banco?.toLowerCase().includes('american')
                            ? 'linear-gradient(135deg, #006FCF 0%, #00A1EB 100%)'
                            : 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
                      borderRadius: '12px',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                    }}>
                      {/* Patrón decorativo */}
                      <Box sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                          {method.tipo === 'tarjeta_credito' ? 'Crédito' : 'Débito'}
                        </Typography>
                        {/* Badge de marca detectada por banco */}
                        <Box sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          px: 1.5, 
                          py: 0.5, 
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}>
                          {method.banco?.toUpperCase() || 'TARJETA'}
                        </Box>
                      </Box>
                      
                      <Typography variant="h5" sx={{ letterSpacing: 3, fontFamily: 'monospace', fontWeight: 'bold' }}>
                        •••• •••• •••• {method.ultimos_digitos}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          TITULAR
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {method.titular}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          VENCE
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {method.mes_expiracion}/{method.anio_expiracion}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {(method.tipo === 'google_pay' || method.tipo === 'mercado_pago') && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    bgcolor: getPaymentColor(method.tipo) + '15', 
                    borderRadius: '10px',
                    border: `2px solid ${getPaymentColor(method.tipo)}40`
                  }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Cuenta vinculada
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: getPaymentColor(method.tipo) }}>
                      {method.email_wallet}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={getPaymentLabel(method.tipo)}
                    size="small"
                    sx={{ 
                      bgcolor: getPaymentColor(method.tipo) + '20', 
                      color: getPaymentColor(method.tipo),
                      fontWeight: 'bold'
                    }}
                  />
                  {method.verificado && (
                    <Chip
                      label="Verificado"
                      size="small"
                      icon={<SecurityIcon />}
                      sx={{ bgcolor: '#e8f5e8', color: '#2e7d32' }}
                    />
                  )}
                </Box>

                {!method.es_principal && (
                  <Button
                    size="small"
                    onClick={() => handleSetDefault(method.id)}
                    sx={{ mt: 2, color: '#2e5a33' }}
                  >
                    Establecer como predeterminada
                  </Button>
                )}
              </CardContent>
            </PaymentCard>
          </Grid>
        ))}

        {/* Card para agregar nueva tarjeta */}
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <AddPaymentCard onClick={handleAddNew}>
            <CardContent sx={{ 
              p: { xs: 3, sm: 4 }, 
              textAlign: 'center',
              minHeight: { xs: '160px', sm: '200px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <AddIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: '#2e5a33', mb: 2 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2e5a33', 
                  mb: 1,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Agregar Método de Pago
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Tarjeta, Google Pay o Mercado Pago
              </Typography>
              
              {/* Mini iconos de métodos */}
              <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Box sx={{ 
                  bgcolor: '#f5f5f5', 
                  borderRadius: '8px', 
                  p: { xs: 0.75, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '32px', sm: '40px' },
                  minHeight: { xs: '32px', sm: '40px' }
                }}>
                  <CreditCardIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: '#666' }} />
                </Box>
                <Box sx={{ 
                  bgcolor: '#000', 
                  borderRadius: '8px', 
                  p: { xs: 0.75, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '32px', sm: '40px' },
                  minHeight: { xs: '32px', sm: '40px' }
                }}>
                  <PaymentIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: '#fff' }} />
                </Box>
                <Box sx={{ 
                  bgcolor: '#00a8ff', 
                  borderRadius: '8px', 
                  p: { xs: 0.75, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '32px', sm: '40px' },
                  minHeight: { xs: '32px', sm: '40px' }
                }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: '#fff' }} />
                </Box>
              </Box>
            </CardContent>
          </AddPaymentCard>
        </Grid>
      </Grid>

      {/* Dialog de opciones de método de pago */}
      <Dialog
        open={optionsDialogOpen}
        onClose={() => setOptionsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: '600px' }
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1, px: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#2e5a33',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Selecciona un método de pago
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary" 
            sx={{ 
              mt: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            Elige cómo quieres pagar tus compras
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {/* Botón Google Pay */}
            <Grid size={{ xs: 12 }}>
              <PaymentOptionButton 
                bgcolor="#000000"
                onClick={() => handleSelectPaymentType('google_pay')}
                sx={{
                  padding: { xs: '12px 16px', sm: '16px 24px' },
                  fontSize: { xs: '14px', sm: '16px' },
                  gap: { xs: '8px', sm: '12px' },
                  opacity: googlePayLoading ? 0.7 : 1,
                  pointerEvents: googlePayLoading ? 'none' : 'auto'
                }}
              >
                {googlePayLoading ? (
                  <CircularProgress size={22} sx={{ color: 'white' }} />
                ) : (
                  <PaymentIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                )}
                <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '14px', sm: '18px' } }}>
                  {googlePayLoading ? 'Conectando...' : 'Vincular Google Pay'}
                </Typography>
              </PaymentOptionButton>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                Pago rápido y seguro con tu cuenta de Google
              </Typography>
            </Grid>

            {/* Botón Mercado Pago */}
            <Grid size={{ xs: 12 }}>
              <PaymentOptionButton 
                bgcolor="#00a8ff"
                onClick={() => handleSelectPaymentType('mercado_pago')}
                sx={{
                  padding: { xs: '12px 16px', sm: '16px 24px' },
                  fontSize: { xs: '14px', sm: '16px' },
                  gap: { xs: '8px', sm: '12px' }
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '14px', sm: '18px' } }}>
                  Vincular Mercado Pago
                </Typography>
              </PaymentOptionButton>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                Paga con tu saldo o tarjetas guardadas en Mercado Pago
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ 
                textAlign: 'center', 
                py: { xs: 1.5, sm: 2 },
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
                my: { xs: 1.5, sm: 2 }
              }}>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  O paga con tarjeta
                </Typography>
              </Box>
            </Grid>

            {/* Botón Tarjeta de Crédito */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <PaymentOptionButton 
                bgcolor="#2e5a33"
                sx={{
                  padding: { xs: '12px 16px', sm: '16px 24px' },
                  fontSize: { xs: '14px', sm: '16px' },
                  gap: { xs: '6px', sm: '8px' }
                }}
                onClick={() => handleSelectPaymentType('tarjeta_credito')}
              >
                <CreditCardIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                <Typography sx={{ 
                  fontWeight: 'bold', 
                  fontSize: { xs: '12px', sm: '14px' }
                }}>
                  Tarjeta de Crédito
                </Typography>
              </PaymentOptionButton>
            </Grid>

            {/* Botón Tarjeta de Débito */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <PaymentOptionButton 
                bgcolor="#4a7c59"
                onClick={() => handleSelectPaymentType('tarjeta_debito')}
                sx={{
                  padding: { xs: '12px 16px', sm: '16px 24px' },
                  fontSize: { xs: '14px', sm: '16px' },
                  gap: { xs: '6px', sm: '8px' }
                }}
              >
                <CreditCardIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                <Typography sx={{ 
                  fontWeight: 'bold', 
                  fontSize: { xs: '12px', sm: '14px' }
                }}>
                  Tarjeta de Débito
                </Typography>
              </PaymentOptionButton>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ 
            mt: { xs: 2, sm: 3 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              🔒 Todos los métodos de pago son seguros y están protegidos
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setOptionsDialogOpen(false)}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para agregar/editar tarjeta */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: '600px' },
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          pt: 3,
          px: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
          color: 'white',
          borderTopLeftRadius: { xs: '16px', sm: '20px' },
          borderTopRightRadius: { xs: '16px', sm: '20px' },
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            {editingMethod ? 'Editar Método de Pago' : `Agregar ${getPaymentLabel(formData.tipo)}`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 2, sm: 3 }, pb: 2, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Apodo (opcional)"
                value={formData.apodo}
                onChange={handleChange('apodo')}
                placeholder="Mi tarjeta principal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: { xs: '10px', sm: '12px' },
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            
            {/* Mostrar tipo seleccionado */}
            {!editingMethod && (
              <Grid size={{ xs: 12 }}>
                <Alert 
                  severity="success"
                  sx={{ 
                    borderRadius: { xs: '10px', sm: '12px' },
                    border: '2px solid #4caf50',
                    backgroundColor: '#f1f8f4',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Has seleccionado: <strong>{getPaymentLabel(formData.tipo)}</strong>
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Campos para wallets digitales */}
            {(formData.tipo === 'google_pay' || formData.tipo === 'mercado_pago') && (
              <>
                {formData.tipo === 'google_pay' && formData.verificado ? (
                  <Grid size={{ xs: 12 }}>
                    <Alert 
                      severity="success" 
                      sx={{ 
                        mb: { xs: 1.5, sm: 2 },
                        borderRadius: { xs: '10px', sm: '12px' },
                        border: '2px solid #4caf50',
                        backgroundColor: '#f1f8f4',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        ✅ Cuenta de Google Pay vinculada exitosamente
                      </Typography>
                    </Alert>
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12 }}>
                    <Alert 
                      severity="info" 
                      sx={{ 
                        mb: { xs: 1.5, sm: 2 },
                        borderRadius: { xs: '10px', sm: '12px' },
                        border: '2px solid #2196f3',
                        backgroundColor: '#e3f2fd',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Vincula tu cuenta de {formData.tipo === 'google_pay' ? 'Google Pay' : 'Mercado Pago'} ingresando el email asociado
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label={`Email de ${formData.tipo === 'google_pay' ? 'Google Pay' : 'Mercado Pago'} *`}
                    type="email"
                    value={formData.email_wallet}
                    onChange={handleChange('email_wallet')}
                    placeholder="tucorreo@gmail.com"
                    disabled={formData.tipo === 'google_pay' && formData.verificado}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: { xs: '10px', sm: '12px' },
                        backgroundColor: formData.tipo === 'google_pay' && formData.verificado ? '#f5f5f5' : '#f8f9fa',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Campos para tarjetas físicas */}
            {(formData.tipo === 'tarjeta_credito' || formData.tipo === 'tarjeta_debito') && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label="Banco *"
                    value={formData.banco}
                    onChange={handleChange('banco')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: { xs: '10px', sm: '12px' },
                        backgroundColor: '#f8f9fa',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  >
                    {bancos.map((banco) => (
                      <MenuItem key={banco} value={banco}>
                        {banco}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {!editingMethod && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ position: 'relative' }}>
                        <TextField
                          fullWidth
                          label="Número de tarjeta *"
                          value={cardNumber}
                          onChange={handleCardNumberChange}
                          placeholder="1234 5678 9012 3456"
                          inputProps={{ maxLength: cardBrandInfo.maxLength + 3 }} // +3 para espacios
                          helperText={
                            detectedCardBrand !== 'unknown' 
                              ? `✅ ${cardBrandInfo.name} detectada - Solo se guardarán los últimos 4 dígitos`
                              : 'Solo se guardarán los últimos 4 dígitos'
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <CardBrandBadge brand={detectedCardBrand} size="medium" />
                              </InputAdornment>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: { xs: '10px', sm: '12px' },
                              backgroundColor: '#f8f9fa',
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              '&:hover': {
                                backgroundColor: '#ffffff',
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#ffffff',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              color: detectedCardBrand !== 'unknown' ? '#2e7d32' : 'inherit'
                            }
                          }}
                        />
                        
                        {/* Mostrar marcas de tarjeta aceptadas */}
                        {cardNumber.length === 0 && (
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            mt: 1, 
                            justifyContent: 'flex-start',
                            flexWrap: 'wrap'
                          }}>
                            <Typography variant="caption" color="textSecondary" sx={{ width: '100%', mb: 0.5 }}>
                              Tarjetas aceptadas:
                            </Typography>
                            <CardBrandBadge brand="visa" size="small" />
                            <CardBrandBadge brand="mastercard" size="small" />
                            <CardBrandBadge brand="amex" size="small" />
                            <CardBrandBadge brand="diners" size="small" />
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={`CVV * (${cardBrandInfo.cvvLength} dígitos)`}
                        value={cvv}
                        onChange={handleCvvChange}
                        type="password"
                        placeholder={detectedCardBrand === 'amex' ? '1234' : '123'}
                        inputProps={{ maxLength: cardBrandInfo.cvvLength }}
                        helperText="No se guarda, solo para validación"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: '10px', sm: '12px' },
                            backgroundColor: '#f8f9fa',
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            '&:hover': {
                              backgroundColor: '#ffffff',
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#ffffff',
                            }
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: { xs: '0.65rem', sm: '0.75rem' }
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Nombre del titular *"
                    value={formData.titular}
                    onChange={handleChange('titular')}
                    placeholder="JUAN PÉREZ"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: { xs: '10px', sm: '12px' },
                        backgroundColor: '#f8f9fa',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Mes expiración *"
                    value={formData.mes_expiracion}
                    onChange={handleChange('mes_expiracion')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: { xs: '10px', sm: '12px' },
                        backgroundColor: '#f8f9fa',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <MenuItem key={month} value={month.toString().padStart(2, '0')}>
                        {month.toString().padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Año expiración *"
                    value={formData.anio_expiracion}
                    onChange={handleChange('anio_expiracion')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: { xs: '10px', sm: '12px' },
                        backgroundColor: '#f8f9fa',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() % 100 + i).map((year) => (
                      <MenuItem key={year} value={year.toString()}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>

          <Alert 
            severity="info" 
            sx={{ 
              mt: { xs: 2, sm: 3 },
              borderRadius: { xs: '10px', sm: '12px' },
              border: '2px solid #2196f3',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backgroundColor: '#e3f2fd',
            }}
          >
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              🔒 <strong>Seguridad:</strong> {
                formData.tipo === 'google_pay' || formData.tipo === 'mercado_pago'
                  ? 'Tu información de pago está protegida por ' + (formData.tipo === 'google_pay' ? 'Google' : 'Mercado Pago') + '. Solo vinculamos tu cuenta mediante el email.'
                  : 'Solo almacenamos los últimos 4 dígitos de tu tarjeta. El número completo y CVV nunca se guardan en nuestros servidores.'
              }
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 }, 
          py: { xs: 1.5, sm: 2 }, 
          backgroundColor: '#f8f9fa',
          gap: { xs: 1, sm: 1.5 },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            fullWidth={true}
            sx={{
              borderRadius: { xs: '10px', sm: '10px' },
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              color: '#666',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#e0e0e0',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            fullWidth={true}
            sx={{
              borderRadius: { xs: '10px', sm: '10px' },
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              width: { xs: '100%', sm: 'auto' },
              background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
              boxShadow: '0 4px 12px rgba(46, 90, 51, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                boxShadow: '0 6px 16px rgba(46, 90, 51, 0.4)',
              },
            }}
          >
            {editingMethod ? 'Actualizar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Alert 
        severity="warning" 
        sx={{ 
          mt: { xs: 2, sm: 3 }, 
          borderRadius: { xs: '12px', sm: '15px' },
          p: { xs: 2, sm: 2 }
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          🔒 Pagos seguros
        </Typography>
        <Typography 
          variant="body2"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          Todas las transacciones son procesadas de forma segura. 
          Solo guardamos los últimos 4 dígitos de tu tarjeta para identificación.
          La información completa nunca se almacena en nuestros servidores.
        </Typography>
      </Alert>

      {/* TODO: Implementar formulario de tarjetas cuando PaymentMethodForm esté disponible */}
      {/* {user && (
        <PaymentMethodForm
          open={cardFormOpen}
          onClose={() => setCardFormOpen(false)}
          userId={user.id}
          onSuccess={handleCardAdded}
        />
      )} */}
    </Box>
  );
};

export default CustomerPayments;
